import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  AlertTriangle,
  Calendar,
  CalendarX,
  Clock,
  Loader2,
  Plus,
  Save,
  Trash2,
  CheckCircle2,
  Ban,
  Link2,
  Copy,
  RefreshCw,
  Unlink,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { useGlobalToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { usePermissions } from "../hooks/usePermissions";

interface Shift {
  start: string;
  end: string;
}

interface DayConfig {
  dayOfWeek: number;
  isWorking: boolean;
  shifts: Shift[];
}

interface Override {
  id: string;
  date: string;
  isWorking: boolean;
  shifts: Shift[];
  reason: string | null;
}

interface Block {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  reason: string | null;
  source: string;
}

interface SpecialistSummary {
  id: string;
  firstName: string;
  lastName: string | null;
  color: string;
}

const DAY_NAMES = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
];

// Reordenamos para mostrar Lunes primero
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const EMPTY_SHIFT: Shift = { start: "09:00", end: "13:00" };
const SECOND_SHIFT: Shift = { start: "16:00", end: "20:00" };

function defaultDays(): DayConfig[] {
  return Array.from({ length: 7 }, (_, dayOfWeek) => ({
    dayOfWeek,
    isWorking: dayOfWeek !== 0, // domingo libre por defecto
    shifts: dayOfWeek === 0 ? [] : [{ ...EMPTY_SHIFT }, { ...SECOND_SHIFT }],
  }));
}

function todayIso(): string {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

export default function SpecialistSchedule() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();
  const { can } = usePermissions();
  const canManage = can("citas:gestionar");
  // El backend exige `servicios:editar` en las rutas de Google Calendar
  // (unificado con la página de Especialistas), a diferencia del resto de
  // controles de esta página que siguen usando `citas:gestionar`.
  const canManageGoogle = can("servicios:editar");

  const [tab, setTab] = useState<"weekly" | "overrides" | "blocks" | "google">(
    "weekly",
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<{
    message: string;
    isBookingModeMismatch: boolean;
  } | null>(null);
  const [specialist, setSpecialist] = useState<SpecialistSummary | null>(null);

  const [days, setDays] = useState<DayConfig[]>(defaultDays());

  const [overrides, setOverrides] = useState<Override[]>([]);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideForm, setOverrideForm] = useState<{
    date: string;
    isWorking: boolean;
    shifts: Shift[];
    reason: string;
  }>({
    date: todayIso(),
    isWorking: false,
    shifts: [],
    reason: "",
  });

  const [blocks, setBlocks] = useState<Block[]>([]);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockForm, setBlockForm] = useState<{
    date: string;
    startTime: string;
    endTime: string;
    reason: string;
  }>({
    date: todayIso(),
    startTime: "12:00",
    endTime: "13:00",
    reason: "",
  });

  // Google Calendar
  const [googleStatus, setGoogleStatus] = useState<{
    connected: boolean;
    googleEmail?: string | null;
    googleCalendarId?: string;
    syncStatus?: string;
    lastError?: string | null;
    lastSyncAt?: string | null;
  } | null>(null);
  const [inviteUrl, setInviteUrl] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [googleSyncing, setGoogleSyncing] = useState(false);

  const loadGoogleStatus = useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiClient.get(`/specialists/${id}/google-calendar`);
      setGoogleStatus(res.data?.data ?? null);
    } catch {
      setGoogleStatus(null);
    }
  }, [id]);

  const loadAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      // Carga crítica: sin el especialista o su horario semanal la página no
      // se puede renderizar con sentido.
      const [specRes, schedRes] = await Promise.all([
        apiClient.get(`/specialists/${id}`),
        apiClient.get(`/specialists/${id}/schedule`),
      ]);
      // google status fuera de la carga crítica para no romper si falla
      loadGoogleStatus();

      const specData = specRes.data?.data;
      if (specData) {
        setSpecialist({
          id: specData.id,
          firstName: specData.firstName,
          lastName: specData.lastName ?? null,
          color: specData.color ?? "#6366f1",
        });
      }

      const apiDays = (schedRes.data?.data?.days ?? []) as DayConfig[];
      if (apiDays.length === 7) {
        setDays(
          apiDays.map((d) => ({
            ...d,
            shifts: Array.isArray(d.shifts) ? d.shifts : [],
          })),
        );
      }

      // Excepciones y bloqueos: no críticos, si fallan solo se avisa con
      // toast para no bloquear el resto de la página.
      try {
        const [ovRes, blkRes] = await Promise.all([
          apiClient.get(`/specialists/${id}/overrides`),
          apiClient.get(`/specialists/${id}/blocks`),
        ]);
        setOverrides(ovRes.data?.data ?? []);
        setBlocks(blkRes.data?.data ?? []);
      } catch (err) {
        showToast(
          extractApiErrorMessage(
            err,
            "Error al cargar las excepciones o los bloqueos",
          ),
          "error",
        );
      }
    } catch (err) {
      const response = (
        err as { response?: { status?: number; data?: unknown } }
      )?.response;
      const status = response?.status;
      const data = response?.data as
        | { code?: string; message?: string }
        | undefined;
      const message = data?.message ?? "";
      const isBookingModeMismatch =
        status === 403 &&
        (data?.code === "BOOKING_MODE_MISMATCH" ||
          message.includes("BOOKING_MODE_MISMATCH"));
      setLoadError({
        message: isBookingModeMismatch
          ? "Esta sección es solo para negocios que trabajan con citas."
          : message || "No se pudo cargar el horario",
        isBookingModeMismatch,
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Detectar query param google_calendar=connected al volver del OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gc = params.get("google_calendar");
    if (gc === "connected") {
      showToast("Google Calendar conectado correctamente");
      setTab("google");
      loadGoogleStatus();
      window.history.replaceState({}, "", window.location.pathname);
    } else if (gc === "error") {
      showToast(
        "Error conectando Google Calendar: " +
          (params.get("reason") ?? "desconocido"),
        "error",
      );
      setTab("google");
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers Google Calendar
  const generateInviteLink = async () => {
    if (!id) return;
    setInviteLoading(true);
    setInviteCopied(false);
    try {
      const res = await apiClient.get(
        `/specialists/${id}/google-calendar/invite-link`,
      );
      setInviteUrl(res.data?.data?.url ?? "");
    } catch (e) {
      showToast(extractApiErrorMessage(e, "Error generando enlace"), "error");
    } finally {
      setInviteLoading(false);
    }
  };

  const copyInviteLink = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2500);
    } catch {
      // ignorar
    }
  };

  const disconnectGoogle = async () => {
    if (!id) return;
    if (
      !(await confirm({
        title: "¿Desconectar Google Calendar?",
        message: "Se eliminarán los bloqueos sincronizados desde Google.",
        confirmText: "Sí, desconectar",
        danger: true,
      }))
    ) {
      return;
    }
    try {
      await apiClient.delete(`/specialists/${id}/google-calendar`);
      showToast("Google Calendar desconectado");
      setGoogleStatus({ connected: false });
      const blkRes = await apiClient.get(`/specialists/${id}/blocks`);
      setBlocks(blkRes.data?.data ?? []);
    } catch (e) {
      showToast(extractApiErrorMessage(e, "Error al desconectar"), "error");
    }
  };

  const forceGoogleSync = async () => {
    if (!id) return;
    setGoogleSyncing(true);
    try {
      const res = await apiClient.post(
        `/specialists/${id}/google-calendar/sync`,
      );
      const { blocksCreated, blocksDeleted } = res.data?.data ?? {};
      showToast(
        `Sincronización completada (${blocksCreated} nuevos, ${blocksDeleted} eliminados)`,
      );
      const blkRes = await apiClient.get(`/specialists/${id}/blocks`);
      setBlocks(blkRes.data?.data ?? []);
      loadGoogleStatus();
    } catch (e) {
      showToast(extractApiErrorMessage(e, "Error al sincronizar"), "error");
    } finally {
      setGoogleSyncing(false);
    }
  };

  // ----- helpers de UI -----

  const toggleDayWorking = (dayOfWeek: number) => {
    setDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek
          ? {
              ...d,
              isWorking: !d.isWorking,
              shifts: !d.isWorking
                ? d.shifts.length
                  ? d.shifts
                  : [{ ...EMPTY_SHIFT }]
                : d.shifts,
            }
          : d,
      ),
    );
  };

  const updateShift = (
    dayOfWeek: number,
    idx: number,
    field: "start" | "end",
    value: string,
  ) => {
    setDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek
          ? {
              ...d,
              shifts: d.shifts.map((s, i) =>
                i === idx ? { ...s, [field]: value } : s,
              ),
            }
          : d,
      ),
    );
  };

  const addShift = (dayOfWeek: number) => {
    setDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek
          ? {
              ...d,
              shifts: [
                ...d.shifts,
                d.shifts.length === 1
                  ? { ...SECOND_SHIFT }
                  : { ...EMPTY_SHIFT },
              ],
            }
          : d,
      ),
    );
  };

  const removeShift = (dayOfWeek: number, idx: number) => {
    setDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek
          ? { ...d, shifts: d.shifts.filter((_, i) => i !== idx) }
          : d,
      ),
    );
  };

  const handleSaveWeekly = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await apiClient.put(`/specialists/${id}/schedule`, { days });
      showToast("Horario guardado correctamente");
    } catch (e) {
      showToast(
        extractApiErrorMessage(e, "No se pudo guardar el horario"),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  // ----- overrides -----

  const openOverrideModal = (existing?: Override) => {
    if (existing) {
      setOverrideForm({
        date: existing.date.slice(0, 10),
        isWorking: existing.isWorking,
        shifts: existing.shifts ?? [],
        reason: existing.reason ?? "",
      });
    } else {
      setOverrideForm({
        date: todayIso(),
        isWorking: false,
        shifts: [],
        reason: "",
      });
    }
    setShowOverrideModal(true);
  };

  const saveOverride = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const res = await apiClient.post(`/specialists/${id}/overrides`, {
        date: overrideForm.date,
        isWorking: overrideForm.isWorking,
        shifts: overrideForm.isWorking ? overrideForm.shifts : [],
        reason: overrideForm.reason || null,
      });
      setShowOverrideModal(false);
      await loadAll();
      showToast("Excepción guardada");
      const warnings = (res.data?.warnings ?? []) as string[];
      if (warnings.length > 0) {
        showToast(warnings.join(" "), "info");
      }
    } catch (e) {
      showToast(
        extractApiErrorMessage(e, "Error al guardar la excepción"),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteOverride = async (overrideId: string) => {
    if (!id) return;
    if (!(await confirm({ title: "¿Eliminar esta excepción?", message: "Se eliminará esta excepción de horario.", confirmText: "Sí, eliminar", danger: true }))) return;
    try {
      await apiClient.delete(`/specialists/${id}/overrides/${overrideId}`);
      await loadAll();
    } catch (e) {
      showToast(extractApiErrorMessage(e, "Error al eliminar"), "error");
    }
  };

  // ----- blocks -----

  const saveBlock = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await apiClient.post(`/specialists/${id}/blocks`, blockForm);
      setShowBlockModal(false);
      setBlockForm({
        date: todayIso(),
        startTime: "12:00",
        endTime: "13:00",
        reason: "",
      });
      await loadAll();
      showToast("Bloqueo creado");
    } catch (e) {
      showToast(
        extractApiErrorMessage(e, "Error al crear el bloqueo"),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteBlock = async (blockId: string) => {
    if (!id) return;
    if (!(await confirm({ title: "¿Eliminar este bloqueo?", message: "Se eliminará el bloqueo de agenda de este profesional.", confirmText: "Sí, eliminar", danger: true }))) return;
    try {
      await apiClient.delete(`/specialists/${id}/blocks/${blockId}`);
      await loadAll();
    } catch (e) {
      showToast(extractApiErrorMessage(e, "Error al eliminar"), "error");
    }
  };

  const orderedDays = useMemo(() => {
    return DAY_ORDER.map(
      (dow) =>
        days.find((d) => d.dayOfWeek === dow) ?? {
          dayOfWeek: dow,
          isWorking: false,
          shifts: [],
        },
    );
  }, [days]);

  const fullName = specialist
    ? `${specialist.firstName} ${specialist.lastName ?? ""}`.trim()
    : "";

  // ============================================================
  // Render
  // ============================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4 p-6 text-center">
        {loadError.isBookingModeMismatch ? (
          <>
            <CalendarX className="w-10 h-10 text-gray-400" />
            <p className="text-gray-600 max-w-sm">{loadError.message}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
            >
              Volver al panel
            </Link>
          </>
        ) : (
          <>
            <AlertTriangle className="w-10 h-10 text-red-500" />
            <p className="text-red-600 max-w-sm">{loadError.message}</p>
            <button
              onClick={loadAll}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Reintentar
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex items-start gap-3">
        <button
          onClick={() => navigate("/specialists")}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          title="Volver"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full inline-block"
              style={{ backgroundColor: specialist?.color ?? "#6366f1" }}
            />
            Horario de {fullName || "profesional"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Configura el horario semanal, vacaciones y bloqueos puntuales
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 flex overflow-x-auto">
          {(
            [
              { key: "weekly", label: "Horario semanal", icon: Clock },
              {
                key: "overrides",
                label: "Vacaciones y excepciones",
                icon: Calendar,
              },
              { key: "blocks", label: "Bloqueos puntuales", icon: Ban },
              { key: "google", label: "Google Calendar", icon: Link2 },
            ] as const
          ).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                tab === t.key
                  ? "border-indigo-600 text-indigo-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* ============= TAB: SEMANAL ============= */}
        {tab === "weekly" && (
          <div className="p-4 md:p-6 space-y-3">
            {orderedDays.map((d) => (
              <div
                key={d.dayOfWeek}
                className={`border rounded-lg p-3 md:p-4 ${
                  d.isWorking ? "border-gray-200" : "border-gray-100 bg-gray-50"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={d.isWorking}
                        disabled={!canManage}
                        onChange={() => toggleDayWorking(d.dayOfWeek)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed" />
                    </label>
                    <span className="font-semibold text-gray-900 w-24">
                      {DAY_NAMES[d.dayOfWeek]}
                    </span>
                    {!d.isWorking && (
                      <span className="text-sm text-gray-500">Libre</span>
                    )}
                  </div>
                  {d.isWorking && (
                    <button
                      type="button"
                      onClick={() => addShift(d.dayOfWeek)}
                      disabled={!canManage}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Tramo
                    </button>
                  )}
                </div>

                {d.isWorking && d.shifts.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {d.shifts.map((s, idx) => (
                      <div key={idx} className="flex items-center gap-2 ml-14">
                        <input
                          type="time"
                          value={s.start}
                          disabled={!canManage}
                          onChange={(e) =>
                            updateShift(
                              d.dayOfWeek,
                              idx,
                              "start",
                              e.target.value,
                            )
                          }
                          className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        <span className="text-gray-400">—</span>
                        <input
                          type="time"
                          value={s.end}
                          disabled={!canManage}
                          onChange={(e) =>
                            updateShift(d.dayOfWeek, idx, "end", e.target.value)
                          }
                          className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        <button
                          type="button"
                          onClick={() => removeShift(d.dayOfWeek, idx)}
                          disabled={!canManage}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Eliminar tramo"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="flex justify-end pt-2">
              <button
                onClick={handleSaveWeekly}
                disabled={saving || !canManage}
                title={canManage ? undefined : "No tienes permiso para gestionar citas"}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar horario
              </button>
            </div>
          </div>
        )}

        {/* ============= TAB: OVERRIDES ============= */}
        {tab === "overrides" && (
          <div className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-600">
                Crea excepciones para vacaciones, días libres o cambios
                puntuales del horario.
              </p>
              <button
                onClick={() => openOverrideModal()}
                disabled={!canManage}
                title={canManage ? undefined : "No tienes permiso para gestionar citas"}
                className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Nueva excepción
              </button>
            </div>

            {overrides.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Calendar className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p>Sin excepciones configuradas</p>
              </div>
            ) : (
              <div className="space-y-2">
                {overrides.map((o) => (
                  <div
                    key={o.id}
                    className="flex items-center justify-between gap-3 p-3 border border-gray-200 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {new Date(o.date).toLocaleDateString("es-ES", {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </span>
                        {!o.isWorking ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                            Día libre
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                            Horario especial
                          </span>
                        )}
                      </div>
                      {o.isWorking && o.shifts?.length > 0 && (
                        <p className="text-sm text-gray-600 mt-1">
                          {o.shifts
                            .map((s) => `${s.start}–${s.end}`)
                            .join(" · ")}
                        </p>
                      )}
                      {o.reason && (
                        <p className="text-sm text-gray-500 italic mt-0.5">
                          {o.reason}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteOverride(o.id)}
                      disabled={!canManage}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ============= TAB: BLOQUEOS ============= */}
        {tab === "blocks" && (
          <div className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-600">
                Bloqueos puntuales (reuniones, comidas…) que impiden citas en
                ese rango.
              </p>
              <button
                onClick={() => setShowBlockModal(true)}
                disabled={!canManage}
                title={canManage ? undefined : "No tienes permiso para gestionar citas"}
                className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Nuevo bloqueo
              </button>
            </div>

            {blocks.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Ban className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p>Sin bloqueos configurados</p>
              </div>
            ) : (
              <div className="space-y-2">
                {blocks.map((b) => (
                  <div
                    key={b.id}
                    className="flex items-center justify-between gap-3 p-3 border border-gray-200 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">
                          {new Date(b.date).toLocaleDateString("es-ES", {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                          })}
                        </span>
                        <span className="text-sm text-gray-700">
                          {b.startTime} – {b.endTime}
                        </span>
                        {b.source === "google" && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                            Google Calendar
                          </span>
                        )}
                      </div>
                      {b.reason && (
                        <p className="text-sm text-gray-500 italic mt-0.5">
                          {b.reason}
                        </p>
                      )}
                    </div>
                    {b.source === "manual" && (
                      <button
                        onClick={() => deleteBlock(b.id)}
                        disabled={!canManage}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ============= TAB: GOOGLE CALENDAR ============= */}
        {tab === "google" && (
          <div className="p-4 md:p-6 space-y-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-sm text-indigo-900">
              <p className="font-medium mb-1">¿Cómo funciona?</p>
              <p>
                Genera un enlace de invitación y envíaselo a este profesional
                por email o WhatsApp. El profesional accede al enlace, autoriza
                con su cuenta de Google y, a partir de ese momento:
              </p>
              <ul className="list-disc pl-5 mt-2 space-y-0.5">
                <li>
                  Las citas creadas en ReservaManager aparecen en su Google
                  Calendar.
                </li>
                <li>
                  Si el profesional crea un evento manual en Google (reunión,
                  comida…), se bloquea ese hueco automáticamente para evitar
                  dobles reservas.
                </li>
              </ul>
              <p className="mt-2 text-xs text-indigo-700">
                El enlace expira en 30 minutos. Si caduca, genera uno nuevo.
              </p>
            </div>

            {googleStatus?.connected ? (
              <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <p className="font-medium text-green-900">
                        Conectado
                        {googleStatus.googleEmail && (
                          <span className="text-green-700 font-normal">
                            {" "}
                            como {googleStatus.googleEmail}
                          </span>
                        )}
                      </p>
                    </div>
                    {googleStatus.lastSyncAt && (
                      <p className="text-xs text-green-700 mt-1">
                        Última sincronización:{" "}
                        {new Date(googleStatus.lastSyncAt).toLocaleString(
                          "es-ES",
                        )}
                      </p>
                    )}
                    {googleStatus.syncStatus === "error" &&
                      googleStatus.lastError && (
                        <p className="text-xs text-red-600 mt-1">
                          Último error: {googleStatus.lastError}
                        </p>
                      )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={forceGoogleSync}
                      disabled={googleSyncing || !canManageGoogle}
                      className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
                    >
                      {googleSyncing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      Sincronizar ahora
                    </button>
                    <button
                      onClick={disconnectGoogle}
                      disabled={!canManageGoogle}
                      className="flex items-center gap-2 px-3 py-2 bg-white border border-red-300 text-red-700 rounded-lg text-sm hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Unlink className="w-4 h-4" />
                      Desconectar
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-3">
                  Este profesional aún no ha conectado su Google Calendar.
                </p>
                {!inviteUrl ? (
                  <button
                    onClick={generateInviteLink}
                    disabled={inviteLoading || !canManageGoogle}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm disabled:opacity-50"
                  >
                    {inviteLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Link2 className="w-4 h-4" />
                    )}
                    Generar enlace de invitación
                  </button>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-600">
                      Envía este enlace al profesional. Caduca en 30 minutos.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={inviteUrl}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono bg-gray-50"
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                      <button
                        onClick={copyInviteLink}
                        className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm flex-shrink-0"
                      >
                        <Copy className="w-4 h-4" />
                        {inviteCopied ? "Copiado" : "Copiar"}
                      </button>
                    </div>
                    <button
                      onClick={generateInviteLink}
                      disabled={!canManageGoogle}
                      className="text-xs text-indigo-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Generar otro enlace
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Override */}
      {showOverrideModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold mb-4">Nueva excepción</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha
                </label>
                <input
                  type="date"
                  value={overrideForm.date}
                  onChange={(e) =>
                    setOverrideForm({ ...overrideForm, date: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={overrideForm.isWorking}
                  onChange={(e) =>
                    setOverrideForm({
                      ...overrideForm,
                      isWorking: e.target.checked,
                      shifts: e.target.checked
                        ? overrideForm.shifts.length
                          ? overrideForm.shifts
                          : [{ ...EMPTY_SHIFT }]
                        : [],
                    })
                  }
                  className="rounded"
                />
                <span className="text-sm">
                  Trabaja con horario especial (si lo desactivas será día libre)
                </span>
              </label>

              {overrideForm.isWorking && (
                <div className="space-y-2 border-t pt-3">
                  {overrideForm.shifts.map((s, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="time"
                        value={s.start}
                        onChange={(e) =>
                          setOverrideForm({
                            ...overrideForm,
                            shifts: overrideForm.shifts.map((sh, i) =>
                              i === idx ? { ...sh, start: e.target.value } : sh,
                            ),
                          })
                        }
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                      />
                      <span>—</span>
                      <input
                        type="time"
                        value={s.end}
                        onChange={(e) =>
                          setOverrideForm({
                            ...overrideForm,
                            shifts: overrideForm.shifts.map((sh, i) =>
                              i === idx ? { ...sh, end: e.target.value } : sh,
                            ),
                          })
                        }
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setOverrideForm({
                            ...overrideForm,
                            shifts: overrideForm.shifts.filter(
                              (_, i) => i !== idx,
                            ),
                          })
                        }
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setOverrideForm({
                        ...overrideForm,
                        shifts: [
                          ...overrideForm.shifts,
                          overrideForm.shifts.length === 1
                            ? { ...SECOND_SHIFT }
                            : { ...EMPTY_SHIFT },
                        ],
                      })
                    }
                    className="flex items-center gap-1 text-sm text-indigo-700 hover:bg-indigo-50 px-2 py-1 rounded"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Tramo
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo (opcional)
                </label>
                <input
                  type="text"
                  value={overrideForm.reason}
                  onChange={(e) =>
                    setOverrideForm({
                      ...overrideForm,
                      reason: e.target.value,
                    })
                  }
                  placeholder="Vacaciones, formación…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowOverrideModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={saveOverride}
                disabled={saving || !canManage}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Block */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold mb-4">Nuevo bloqueo</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha
                </label>
                <input
                  type="date"
                  value={blockForm.date}
                  onChange={(e) =>
                    setBlockForm({ ...blockForm, date: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Desde
                  </label>
                  <input
                    type="time"
                    value={blockForm.startTime}
                    onChange={(e) =>
                      setBlockForm({ ...blockForm, startTime: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hasta
                  </label>
                  <input
                    type="time"
                    value={blockForm.endTime}
                    onChange={(e) =>
                      setBlockForm({ ...blockForm, endTime: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo (opcional)
                </label>
                <input
                  type="text"
                  value={blockForm.reason}
                  onChange={(e) =>
                    setBlockForm({ ...blockForm, reason: e.target.value })
                  }
                  placeholder="Reunión, comida…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowBlockModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={saveBlock}
                disabled={saving || !canManage}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Crear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
